// Pure helpers for the vehicle-history pages. No React, no network — everything here is a
// function of its arguments, which is what makes `utils.test.js` worth having.
//
// The ones that matter are about MONEY. This section is the only place in the product that shows
// what a report costs us and what we make on it, so a margin coloured wrongly or a price rounded
// wrongly is a pricing decision taken on bad information.

// --- Vocabulary ------------------------------------------------------------------------------
// Wire values, from `vehicle_history/constants.py`. Never translated in the browser: the server
// owns these strings and the app, the site and this dashboard all key on them.

export const STATUS_LABELS = {
  draft: 'Draft',
  awaiting_payment: 'Awaiting payment',
  awaiting_confirmation: 'Awaiting confirmation',
  processing: 'Getting the report',
  ready: 'Ready',
  partial: 'Partly ready',
  no_data: 'No records found',
  failed: 'Failed',
  closed: 'Closed'
}

// `no_data` is NOT red. A car with nothing on file is a normal outcome and often useful news to
// the customer; colouring it like a failure would have the team apologising for working software.
export const STATUS_COLORS = {
  draft: 'default',
  awaiting_payment: 'gold',
  awaiting_confirmation: 'blue',
  processing: 'processing',
  ready: 'green',
  partial: 'orange',
  no_data: 'default',
  failed: 'red',
  closed: 'default'
}

export const PART_LABELS = {
  queued: 'Queued',
  requested: 'Requested',
  processing: 'Getting it',
  ready: 'Ready',
  partial: 'Partly ready',
  no_data: 'No records',
  failed: 'Failed',
  awaiting_funds: 'Waiting for credit',
  cancelled: 'Cancelled'
}

// `awaiting_funds` is gold, not red: nothing failed and nothing was charged. The supplier refused
// for credit, the money is still ours to spend, and a top-up releases it.
export const PART_COLORS = {
  queued: 'default',
  requested: 'blue',
  processing: 'processing',
  ready: 'green',
  partial: 'orange',
  no_data: 'default',
  failed: 'red',
  awaiting_funds: 'gold',
  cancelled: 'default'
}

export const MARKET_LABELS = {
  north_america: 'North America',
  china: 'China',
  japan: 'Japan',
  korea: 'Korea',
  europe: 'Europe',
  russia_belarus: 'Russia & Belarus',
  oem: 'Manufacturer records'
}

// The order the Products page groups by. China first because it is 32% of our paying customers'
// cars — the largest single market this service exists for.
export const MARKET_ORDER = [
  'north_america',
  'china',
  'japan',
  'korea',
  'europe',
  'oem',
  'russia_belarus'
]

export const IDENTIFIER_LABELS = {
  vin: 'VIN',
  jp_chassis: 'Japanese chassis number'
}

export const COST_SOURCE_LABELS = {
  public_pricing: 'Public price list',
  charged: 'What we were charged',
  documented: 'Supplier documentation',
  manual: 'Entered by hand',
  assumed: 'Assumed'
}

export const ALERT_LABELS = {
  misconfigured: 'Supplier misconfigured',
  balance_empty: 'Balance empty',
  balance_critical: 'Balance critical',
  balance_low: 'Balance low',
  carfax_credits_low: 'Carfax credits low',
  upstream_usa_low: 'GlobalVIN\'s own supply low',
  usa_health_down: 'USA service down',
  service_degraded: 'Service keeps failing',
  price_changed: 'Price changed',
  margin_low: 'Margin below its floor',
  awaiting_funds: 'Report waiting for credit',
  refund_due: 'Refund due',
  part_failed: 'Part of a report failed',
  identifier_rejected: 'Supplier refused the number',
  stuck: 'Stuck past its deadline',
  unconfirmed_paid: 'Paid, not confirmed',
  double_payment: 'Paid twice',
  payment_after_close: 'Paid after closing'
}

export const PRIORITY_COLORS = { urgent: 'red', high: 'orange', low: 'default' }

export const ACTION_LABELS = {
  created: 'Created',
  finalized: 'Price locked',
  paid: 'Paid',
  identifier_changed: 'Number changed',
  submitted: 'Submitted to the supplier',
  part_requested: 'Report requested',
  part_retried: 'Report retried',
  part_completed: 'Report finished',
  product_switched: 'Report switched',
  manual_refund: 'Refund recorded',
  note: 'Note',
  closed: 'Closed'
}

// Each activation error, as the sentence the owner reads beside the switch. The server answers
// codes; a code on a screen is a developer talking to a shopkeeper.
export const ACTIVATION_ERRORS = {
  no_price: 'Set a customer price first.',
  bad_currency: 'That currency is not one we sell in. Pick GHS or USD.',
  price_out_of_range:
    'That price is outside what the dashboard allows — check for a mistyped figure.',
  no_components: 'Add at least one supplier report to this package.',
  unknown_cost:
    'One of its reports has no known cost yet, so the margin cannot be worked out. Buy one with a test purchase, or type the cost in on the Supplier page.',
  identifier_kind_mismatch:
    'Its reports need a different kind of number from the package. A Japanese auction report needs a chassis number; the rest need a VIN.'
}

export const activationSentences = (codes = []) =>
  (codes || []).map(
    code => ACTIVATION_ERRORS[code] || `This package cannot go on sale yet (${code}).`
  )

export const statusLabel = status => STATUS_LABELS[status] || status || '—'
export const statusColor = status => STATUS_COLORS[status] || 'default'
export const partLabel = status => PART_LABELS[status] || status || '—'
export const partColor = status => PART_COLORS[status] || 'default'
export const marketLabel = market => MARKET_LABELS[market] || market || '—'
export const alertLabel = kind => ALERT_LABELS[kind] || kind || '—'
export const actionLabel = kind => ACTION_LABELS[kind] || kind || '—'

// --- Money -----------------------------------------------------------------------------------
// Every figure crosses the wire as a two-decimal STRING, never a float. Parsing to a number for
// display is fine; sending one back is not.

export const money = (amount, currency = 'GHS') => {
  if (amount === null || amount === undefined || amount === '') return '—'
  const number = Number(amount)
  if (Number.isNaN(number)) return '—'
  return `${currency || ''} ${number.toFixed(2)}`.trim()
}

export const usd = amount => money(amount, '$').replace('$ ', '$')

export const toMoneyString = value => {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  return Number.isNaN(number) ? '' : number.toFixed(2)
}

export const percent = value => {
  if (value === null || value === undefined || value === '') return '—'
  const number = Number(value)
  return Number.isNaN(number) ? '—' : `${number.toFixed(1)}%`
}

// Red below the floor, amber within five points of it, green above. The floor is the product's own
// when it has one, because a $1 window sticker and a $28 European report do not warrant the same.
export const marginColor = (marginPercent, floor) => {
  if (marginPercent === null || marginPercent === undefined || marginPercent === '') {
    return undefined
  }
  const value = Number(marginPercent)
  const limit = Number(floor === null || floor === undefined ? 30 : floor)
  if (Number.isNaN(value)) return undefined
  if (value < limit) return 'red'
  if (value < limit + 5) return 'orange'
  return 'green'
}

// --- The live margin estimate (Req 2.3) --------------------------------------------------------
//
// The form shows the API's figures for the SAVED state and a clearly marked estimate for unsaved
// edits. The estimate is computed from the API's own `costUsd` and the rate the API returned —
// never from a rate the browser fetched or guessed, because two sources for one exchange rate is
// two different margins on one screen.
//
// `ghsToUsd` is CEDIS PER DOLLAR, the same direction the API uses: at 15, a $6 cost is GHS 90.
export const estimateCostIn = (costUsd, currency, ghsToUsd) => {
  if (costUsd === null || costUsd === undefined || costUsd === '') return null
  const cost = Number(costUsd)
  if (Number.isNaN(cost)) return null
  if (currency === 'USD') return cost
  if (currency === 'GHS') {
    const rate = Number(ghsToUsd)
    if (!rate || Number.isNaN(rate)) return null
    return cost * rate
  }
  return null
}

export const estimateMargin = ({ priceAmount, priceCurrency, costUsd, ghsToUsd }) => {
  const price = Number(priceAmount)
  if (!priceAmount || Number.isNaN(price) || price <= 0) return null
  const cost = estimateCostIn(costUsd, priceCurrency, ghsToUsd)
  if (cost === null) return null
  const amount = price - cost
  return { amount, percent: (amount / price) * 100 }
}

// The cost of a set of components, from the services the API listed. Null when any one of them has
// no known cost — never zero, which would show a 100% margin on a package nobody can price.
export const componentsCostUsd = (componentServiceIds = [], services = []) => {
  const byId = {}
  ;(services || []).forEach(service => {
    byId[service.id] = service
  })
  let total = 0
  for (const id of componentServiceIds || []) {
    const service = byId[id]
    if (!service || service.costUsd === null || service.costUsd === undefined) return null
    const cost = Number(service.costUsd)
    if (Number.isNaN(cost)) return null
    total += cost
  }
  return componentServiceIds && componentServiceIds.length ? total : null
}

// --- The refund modal's validation (Req 3.3) ---------------------------------------------------
//
// The reference is required because it is the thing that makes a refund idempotent: the server
// refuses the same Paystack reference twice, and that constraint is the only thing standing
// between two people on the same screen and two refunds.
export const validateRefund = ({ amount, currency, reference, maxAmount }) => {
  const errors = {}
  const value = Number(amount)

  if (amount === null || amount === undefined || amount === '') {
    errors.amount = 'How much was refunded?'
  } else if (Number.isNaN(value) || value <= 0) {
    errors.amount = 'Enter an amount greater than zero.'
  } else if (String(amount).includes('.') && String(amount).split('.')[1].length > 2) {
    // Before the maximum check: `200.005` against a GHS 200.00 price is a mistyped figure, and
    // "that is more than the customer paid" sends the typist looking in the wrong place.
    errors.amount = 'Two decimal places at most.'
  } else if (maxAmount !== null && maxAmount !== undefined && value > Number(maxAmount)) {
    errors.amount = `That is more than the customer paid (${money(maxAmount, currency)}).`
  }

  if (!currency) errors.currency = 'Which currency?'
  if (!reference || !String(reference).trim()) {
    errors.reference =
      'Paste the Paystack reference of the money that moved. It is what stops this refund being recorded twice.'
  }
  return errors
}

export const isValidRefund = values => Object.keys(validateRefund(values)).length === 0

// --- Dates -------------------------------------------------------------------------------------
const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short'
})

const parsed = iso => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

export const formatDateTime = iso => {
  const date = parsed(iso)
  return date ? DATE_TIME_FORMAT.format(date) : '—'
}

const MINUTE = 60000
const HOUR = 60 * MINUTE
const TWO_DAYS = 48 * HOUR

export const formatAge = (iso, nowMs = Date.now()) => {
  const date = parsed(iso)
  if (!date) return '—'
  const elapsed = nowMs - date.getTime()
  if (elapsed < MINUTE) return '<1m'
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`
  if (elapsed < TWO_DAYS) return `${Math.floor(elapsed / HOUR)}h`
  return `${Math.floor(elapsed / (24 * HOUR))}d`
}

// How long a report took, for the parts table. Submitted to finished, in the shortest honest form.
export const duration = (fromIso, toIso) => {
  const from = parsed(fromIso)
  const to = parsed(toIso)
  if (!from || !to) return '—'
  const elapsed = to.getTime() - from.getTime()
  if (elapsed < 0) return '—'
  if (elapsed < MINUTE) return `${Math.round(elapsed / 1000)}s`
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`
  return `${Math.floor(elapsed / HOUR)}h ${Math.floor((elapsed % HOUR) / MINUTE)}m`
}

// --- The orders query --------------------------------------------------------------------------
//
// Query parameters are snake_case: w-api camelizes BODIES, not query strings. A camelCase filter
// here is silently ignored by the server, which looks exactly like a filter that found nothing.
// Where the order was placed. Worth a column of its own because it changes how an order is
// worked: a WhatsApp order has a chat we can open and a person on the other end of it, an app
// order has neither.
export const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  app: 'App',
  web: 'Website',
  admin: 'Our team'
}

export const CHANNEL_COLORS = {
  whatsapp: 'green',
  app: 'blue',
  web: 'geekblue',
  admin: 'default'
}

export const channelLabel = channel => CHANNEL_LABELS[channel] || channel || '—'

// Only what was actually delivered, in the order it was tried. "Pending" is not a failure and is
// not dressed as one: the notifier writes the row before it sends.
export const DELIVERY_LABELS = {
  sent: 'Sent',
  failed: 'Failed',
  pending: 'Sending'
}

export const DELIVERY_COLORS = { sent: 'green', failed: 'red', pending: 'gold' }

export const deliveryLabel = status => DELIVERY_LABELS[status] || status || '—'

// A re-send is offered only where it would actually do something: the report has to exist and the
// customer has to have a conversation to receive it in. Offering it otherwise promises a customer
// something the server will refuse.
export const canResend = (order = {}) =>
  Boolean(order.paid) &&
  Boolean(order.conversationId) &&
  (order.parts || []).some(part => part.hasFile)

export const buildOrdersQuery = (filters = {}) => {
  const params = new URLSearchParams()
  const {
    status,
    product,
    market,
    channel,
    paid,
    attention,
    q,
    from,
    to,
    page
  } = filters

  if (status && status.length) params.set('status', [].concat(status).join(','))
  if (product) params.set('product', product)
  if (market) params.set('market', market)
  if (channel && channel.length) params.set('channel', [].concat(channel).join(','))
  if (paid === true || paid === 'true') params.set('paid', 'true')
  if (paid === false || paid === 'false') params.set('paid', 'false')
  if (attention) params.set('attention', 'true')
  if (q && String(q).trim()) params.set('q', String(q).trim())
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (page && page > 1) params.set('page', String(page))

  const query = params.toString()
  return query ? `?${query}` : ''
}

// --- Server errors ----------------------------------------------------------------------------
//
// The server's own sentence, which is always better than ours: it knows which refund reference was
// a duplicate and which scope is unapproved (memory `w-admin-conventions`).
export const serverMessage = (response, fallback = 'That did not work. Try again.') => {
  const data = ((response || {}).error || {}).response
  const body = (data || {}).data || {}
  if (typeof body === 'string' && body.trim()) return body
  return body.detail || body.error || body.message || firstFieldError(body) || fallback
}

// DRF's validation 400 is keyed by field — `{components: [{label: ['This field may not be
// blank.']}]}` — with no `detail`. The first sentence in it, named by its field, beats a generic
// "could not save" that leaves the operator guessing which box is wrong.
const firstFieldError = (body, field = '') => {
  if (typeof body === 'string') return field ? `${field}: ${body}` : body
  if (Array.isArray(body)) {
    for (const item of body) {
      const found = firstFieldError(item, field)
      if (found) return found
    }
    return ''
  }
  if (body && typeof body === 'object') {
    for (const [key, value] of Object.entries(body)) {
      if (key === 'code') continue
      const found = firstFieldError(value, key === 'nonFieldErrors' ? field : key)
      if (found) return found
    }
  }
  return ''
}

export const serverCode = response => {
  const data = ((response || {}).error || {}).response
  return (((data || {}).data) || {}).code || ''
}

// The activation refusal carries its reasons in the error body, not in the message.
export const serverActivationErrors = response => {
  const data = ((response || {}).error || {}).response
  const body = ((data || {}).data) || {}
  return Array.isArray(body.activationErrors) ? body.activationErrors : []
}
